import type { Child, ChildrenResponse, CreateChildData, AddParentData, ChildParent } from '@/types/child'
import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, data.error || 'Request failed', data)
  }

  return data
}

export const childrenApi = {
  async list(): Promise<ChildrenResponse> {
    return fetchWithAuth<ChildrenResponse>('/api/children')
  },

  async get(id: string): Promise<{ child: Child }> {
    return fetchWithAuth<{ child: Child }>(`/api/children/${id}`)
  },

  async create(data: CreateChildData): Promise<{ child: Child }> {
    return fetchWithAuth<{ child: Child }>('/api/children', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async update(id: string, data: Partial<CreateChildData>): Promise<{ child: Child }> {
    return fetchWithAuth<{ child: Child }>(`/api/children/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async delete(id: string): Promise<{ success: boolean }> {
    return fetchWithAuth<{ success: boolean }>(`/api/children/${id}`, {
      method: 'DELETE',
    })
  },

  async addParent(childId: string, data: AddParentData): Promise<{ parent: ChildParent }> {
    return fetchWithAuth<{ parent: ChildParent }>(`/api/children/${childId}/parents`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async removeParent(childId: string, parentId: string): Promise<{ success: boolean }> {
    return fetchWithAuth<{ success: boolean }>(`/api/children/${childId}/parents/${parentId}`, {
      method: 'DELETE',
    })
  },

  async uploadPhoto(childId: string, file: File): Promise<{ photoUrl: string }> {
    const accessToken = useAuthStore.getState().accessToken
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_URL}/api/children/${childId}/photo`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: formData,
    })

    const data = await response.json()
    if (!response.ok) {
      throw new ApiError(response.status, data.error || 'Failed to upload photo', data)
    }
    return data
  },

  async deletePhoto(childId: string): Promise<{ success: boolean }> {
    return fetchWithAuth<{ success: boolean }>(`/api/children/${childId}/photo`, {
      method: 'DELETE',
    })
  },
}

export { ApiError }
